import { css } from '@emotion/css';
import { Button, Checkbox, Collapse, Divider, Input, Modal, Tooltip, message } from 'antd';
import { MercadoFAQ, MercadoItemFAQQuestion, answerTheQuestion, deleteQuestion } from './services';
import { useEffect, useRef, useState } from 'react';
import {
  useClickAway,
  useLocalStorageState,
  useMemoizedFn,
  useRequest,
  useUpdateEffect,
} from 'ahooks';
import { LoadingOutlined, PlusOutlined, RightOutlined } from '@ant-design/icons';
import { overseaTranslate } from 'services/api/overseaTranslate';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import dayjs from 'lib/dayjs';
import useToggleShowTranslation from './useToggleShowTranslation';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { ReactComponent as AutoTranslateSvg } from 'views/Conversation/assets/autoTranslate.svg';
import Icon from '@ant-design/icons/lib/components/Icon';
import { PhrasesRootV2, PhrasesTextElement, PhraseAttachmentElement } from 'services/api/phrase';
import { PhraseValue, usePhraseOnChat } from 'hooks/business/usePhraseOnChat';
import ActivePopoverV2 from 'views/Conversation/Chat/components/Phrase/ActivePopoverV2';
import NewActivePopover from 'views/Conversation/Chat/components/Phrase/NewActivePopover';
import PassivePopover from 'views/Conversation/Chat/components/Phrase/PassivePopover';
import { noop } from 'lodash-es';
import { useReactRouterLoction } from 'views/Multitab/Router';
import { useBuyerFAQContext } from './BuyerFAQ';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import StatusButton from 'components/StatusButton';
import styled from '@emotion/styled';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { useLlmModal } from './LlmModal';
import { useQAPairDrawer } from './QAPairDrawer';
import { useAnswerOriginLabel } from './Filter';
import { submitSupplementKnowledge } from 'services/api/supplementKnowledge';

const MAX_LENGTH = 2000;

const collapseCls = css`
  width: 100%;
  flex: 1;
  .ant-collapse-item {
    padding-right: 12px;
    > .ant-collapse-header {
      padding-bottom: 0;
      .ant-collapse-arrow {
        margin-top: 6px;
      }
    }
    > .ant-collapse-content-box {
      padding-bottom: 0;
    }
  }
  & + & {
    .ant-collapse-header {
      .header {
        border-top: 1px dashed var(--fett-border-strong);
        padding-top: 12px;
      }
    }
  }
  textarea.ant-input {
    border: none;
  }
`;
interface TranslationButtonProps {
  active?: boolean;
  loading: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const TranslationButton = (props: TranslationButtonProps) => {
  const { active, loading, onClick, disabled } = props;
  if (loading) {
    return <LoadingOutlined className="w-4 h-4 cursor-not-allowed" />;
  }
  return (
    <Icon
      component={AutoTranslateSvg}
      className={active ? '!text-primary cursor-pointer w-4 h-4 ' : undefined}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick();
      }}
    />
  );
};

const saveAsItemKnowledgeStoreKey = '@fett/mercadoQASaveAsItemKnowledge';

interface Props {
  preview?: boolean;
  qa: MercadoFAQ;
  questionItem: MercadoItemFAQQuestion;
  phraseData?: PhrasesRootV2;
  refreshList?: () => void;
  onChange: (faq?: MercadoItemFAQQuestion) => void;
  expand: boolean;
  onExpandChange: (expand: boolean) => void;
  expandAll?: boolean;
}

const QuestionItem = (props: Props) => {
  const {
    preview,
    qa,
    questionItem,
    refreshList,
    expand: expandProp,
    onExpandChange,
    expandAll,
  } = props;
  const { region, storeId, userId, dialogReply } = questionItem;
  const [mode, setMode] = useState<'view' | 'input'>('view');
  const [inputText, setInputText] = useState('');
  const { t, answerOriginLabelMap } = useAnswerOriginLabel();
  const lang = useSystemLanguageValue();
  const { whiteListConfig: { MERCADO_QA_LLM } = {} } = useOrgConfigStateValue();
  const aiPermission = useGetPermission()('AI智能接待');

  const [expand, setExpand] = useState(expandAll || expandProp);

  useUpdateEffect(() => {
    setExpand(expandAll || expandProp);
  }, [expandAll]);

  const {
    text: question,
    onToggle: toggleQuestion,
    loading: questionTranslating,
  } = useToggleShowTranslation(questionItem.text, questionItem.textTranslate, lang);
  const {
    text: answer,
    onToggle: toggleAnswer,
    loading: answerTranslating,
  } = useToggleShowTranslation(questionItem.answer?.text, questionItem.answer?.textTranslate, lang);

  const [saveAsItemKnowledge, setSaveAsItemKnowledge] = useLocalStorageState<boolean>(
    saveAsItemKnowledgeStoreKey,
    { defaultValue: false },
  );

  const { runAsync: onDeleteQuestion, loading: deleting } = useRequest(
    async () => {
      try {
        await deleteQuestion(questionItem.id);
        message.success(t('已删除'));
        refreshList?.();
      } catch (error) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    },
    {
      manual: true,
    },
  );
  const { runAsync: onBlockBuyer, loading: blocking } = useRequest(
    async () => {
      try {
        message.success(t('已屏蔽'));
      } catch (error) {
        message.error(t('translation.操作失败，请稍后再试'));
      }
    },
    { manual: true },
  );
  const { runAsync: submitAnswer, loading: submitting } = useRequest(
    () => answerTheQuestion(questionItem.id, inputText),
    {
      manual: true,
      onSuccess: () => {
        if (MERCADO_QA_LLM && questionItem.dialogReply?.isProductQuestion && saveAsItemKnowledge) {
          submitSupplementKnowledge({
            source: 'MERCADO_QA',
            externalQuestionId: questionItem.id,
          })
            .then(() => {
              message.success(t('phrase.保存成功'));
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        } else {
          refreshList?.();
        }
        message.success(t('已提交'));
      },
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );
  const { runAsync: translateInput, loading: inputTranslating } = useRequest(
    async () => {
      if (inputText === dialogReply?.candidateTranslated) {
        setInputText(dialogReply.candidateText);
        return Promise.resolve(dialogReply.candidateTranslated);
      }
      const result = await overseaTranslate(
        inputText,
        regionLanguageMap[toLowerCase(questionItem.region)],
        'messageEditor',
      );
      setInputText(() => result);
      return result;
    },
    {
      manual: true,
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );
  const { runAsync: translateAndAnswer, loading: translateAndAnswerLoading } = useRequest(
    async () => {
      const translateText = await translateInput();
      await answerTheQuestion(questionItem.id, translateText).then(() => {
        if (MERCADO_QA_LLM && questionItem.dialogReply?.isProductQuestion && saveAsItemKnowledge) {
          submitSupplementKnowledge({
            source: 'MERCADO_QA',
            externalQuestionId: questionItem.id,
          })
            .then(() => {
              message.success(t('phrase.保存成功'));
            })
            .catch(() => {
              message.error(t('phrase.保存失败，请稍后再试'));
            });
        } else {
          refreshList?.();
        }
      });
    },
    {
      manual: true,
      onSuccess: () => {
        message.success(t('已提交'));
      },
      onError: () => {
        message.error(t('translation.操作失败，请稍后再试'));
      },
    },
  );

  const {
    phraseGroups,
    phrasesByGroup,
    phraseFilter,
    isRelateRegion,
    updatePhraseFilter,
    phraseList,
  } = usePhraseOnChat('MERCADO', region, storeId);

  const onApplyPhrase = useMemoizedFn((phrase?: PhraseValue, append = true) => {
    if (!phrase) return;

    const { textEle, restEles } = phrase.phraseElems.elems.reduce(
      (res, ele: any) => {
        if (ele.attachment) {
          res.restEles.push(ele);
        } else if (ele.text) {
          if (res.textEle) {
            res.restEles.push(ele);
          } else {
            res.textEle = ele;
          }
        }
        return res;
      },
      {
        restEles: [],
      } as {
        textEle?: PhrasesTextElement;
        restEles: (PhrasesTextElement | PhraseAttachmentElement)[];
      },
    );

    if (textEle) {
      const content = textEle.text.translationContent || textEle.text.content;
      setInputText((text) => (append ? `${text}${content}` : content));
    }

    const restHasAttachment = restEles.some((ele: any) => ele.attachment);
    const restHasText = restEles.some((ele: any) => ele.text);

    if (restHasAttachment && restHasText) {
      message.warn(t('Mercado Q&A 回复不支持使用附件和多条消息'));
    } else if (restHasAttachment) {
      message.warn(t('Mercado Q&A 回复不支持使用附件'));
    } else if (restHasText) {
      message.warn(t('Mercado Q&A 回复不支持使用多条消息'));
    }
  });
  const [focus, setFocus] = useState(false);
  const contentClickRef = useRef(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  useClickAway(() => {
    setTimeout(() => {
      if (contentClickRef.current) {
        contentClickRef.current = false;
      } else {
        setFocus(false);
      }
    }, 200);
  }, triggerRef);
  const { pathname } = useReactRouterLoction();
  useEffect(() => {
    if (!pathname.startsWith('/workbench/mercado-faq')) {
      setFocus(false);
    }
  }, [pathname]);
  const { show } = useBuyerFAQContext();
  const { whiteListConfig: { NEW_PHRASE } = {} } = useOrgConfigStateValue();
  const ActivePopover = NEW_PHRASE ? NewActivePopover : ActivePopoverV2;

  const [llmModal, openLlmModal] = useLlmModal();
  const [qaPairDrawer, openQAPairDrawer] = useQAPairDrawer();

  const renderStill = (questionTime: number, answerTime: number) => {
    const duration = dayjs.duration(dayjs(answerTime).diff(dayjs(questionTime)));
    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    if (days) {
      return t('{x}天{y}小时{z}分钟', {
        x: days,
        y: hours,
        z: minutes,
      });
    }
    if (hours) {
      return t('{y}小时{z}分钟', {
        y: hours,
        z: minutes,
      });
    }
    if (minutes) {
      return `${minutes} ${t('marketing.分钟')}`;
    }
    return `${seconds} ${t('translation.秒')}`;
  };

  return (
    <Collapse
      ghost
      expandIconPosition="right"
      className={collapseCls}
      activeKey={(expandAll ? expand : expandProp) || preview ? '1' : undefined}
      onChange={(keys) => {
        const expand = !!(keys as any).length;
        onExpandChange(expand);
        setExpand(expand);
      }}
      expandIcon={preview ? () => null : undefined}
    >
      <Collapse.Panel
        header={
          <div className="w-full flex gap-24 justify-between items-start header">
            <div className="flex items-start gap-2">
              <div className="h-5 leading-5 flex-none  flex justify-center items-center">
                <div className="text-primary border-primary border border-solid w-4 h-4 rounded-sm text-sm font-bold flex justify-center items-center">
                  Q
                </div>
              </div>
              <div className="flex-1 leading-5 flex gap-2 ">
                <span className="flex-1">{question}</span>
                <span className="flex-none text-[var(--fett-text-tertiary)]">
                  {dayjs(Number(questionItem.createTime)).format('YYYY-MM-DD HH:mm:ss')}
                </span>
                <span className="flex-none" onClick={(e) => e.stopPropagation()}>
                  <TranslationButton
                    active
                    onClick={toggleQuestion}
                    loading={questionTranslating}
                  />
                </span>
              </div>
            </div>
            <div
              style={preview ? { display: 'none' } : undefined}
              className="flex-none"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-[var(--fett-text-tertiary)] text-xs">
                {questionItem.userNick}({questionItem.userId})
              </span>
              <Button
                type="text"
                className="!p-0 !h-6 !text-xs ml-3 !hidden"
                onClick={() => {
                  Modal.confirm({
                    title: t('确认要屏蔽(x)吗？', { x: questionItem.userId }),
                    content: t('屏蔽后该买家将不可以再发起提问'),
                    onOk: onBlockBuyer,
                  });
                }}
                size="small"
                disabled={blocking}
              >
                {t('屏蔽买家')}
              </Button>
              <Button
                className="!p-0 !h-6 !text-xs ml-3"
                size="small"
                type="text"
                onClick={() => show({ storeId, buyerId: userId })}
              >
                {t('查看其他订单和Q&A')}
              </Button>
              <Button
                type="text"
                className="!p-0 !h-6 !text-xs ml-3"
                size="small"
                onClick={() => {
                  Modal.confirm({
                    title: t('确认要删除此问题吗？'),
                    content: t('删除后问题不可恢复'),
                    onOk: onDeleteQuestion,
                  });
                }}
                disabled={deleting}
              >
                {t('删除问题')}
              </Button>
            </div>
          </div>
        }
        key="1"
      >
        {MERCADO_QA_LLM && dialogReply?.replied && qa.item.itemStatus === 'NORMAL' && (
          <DialogReply>
            <header>
              <h4>{t('AI 推荐答案')}</h4>
              <div>
                {aiPermission && dialogReply.replyMode !== 'ITEM_AI' && (
                  <Button
                    type="link"
                    onClick={() =>
                      openLlmModal({
                        region: questionItem.region,
                        storeId: questionItem.storeId,
                        storeGroupId: dialogReply.scenarioContext.storeGroupId,
                        storeGroupName: dialogReply.scenarioContext.storeGroupName,
                        intentId: dialogReply.scenarioContext.llmBasicInfo.llmIntentId,
                        policyId: dialogReply.scenarioContext.llmBasicInfo.llmPolicyId,
                        subPolicyId: dialogReply.scenarioContext.llmBasicInfo.llmSubPolicyId,
                      })
                    }
                  >
                    {t('调整话术')}
                    <RightOutlined />
                  </Button>
                )}
                {aiPermission && dialogReply.replyMode === 'ITEM_AI' && (
                  <Button
                    type="link"
                    onClick={() =>
                      openQAPairDrawer({
                        question: questionItem,
                        externalItemId: qa.item.externalItemId,
                      })
                    }
                  >
                    {t('AI 有误，调整答案')}
                    <RightOutlined />
                  </Button>
                )}
                {llmModal}
                {qaPairDrawer}
                <StatusButton
                  type="link"
                  onClick={() =>
                    answerTheQuestion(
                      questionItem.id,
                      dialogReply.candidateText,
                      dialogReply.dialogResultId,
                    ).then(() => {
                      refreshList?.();
                      message.success(t('已提交'));
                    })
                  }
                >
                  {t('提交答案')}
                </StatusButton>
              </div>
            </header>
            <p>{dialogReply.candidateText}</p>
            <Divider dashed />
            <p>{dialogReply.candidateTranslated}</p>
          </DialogReply>
        )}
        <div className="w-full flex gap-2 items-start">
          <div className="h-5 leading-5 flex-none  flex justify-center items-center">
            <div className="text-primary border-primary border border-solid w-4 h-4 rounded-sm text-sm font-bold flex justify-center items-center">
              A
            </div>
          </div>
          {mode === 'input' ? (
            <div className="flex-1">
              <div className="w-full border border-[var(--fett-border-strong)] rounded border-solid">
                <div className="p-2 border-0 border-b border-b-[var(--fett-border-default)] border-solid relative flex">
                  <ActivePopover
                    placement="topLeft"
                    channel="MERCADO"
                    mercadoQAmode
                    region={region}
                    conversationToggle={false}
                    phraseList={phraseList}
                    phraseFilter={phraseFilter}
                    phraseGroups={phraseGroups}
                    phrasesByGroup={phrasesByGroup}
                    updatePhraseFilter={updatePhraseFilter}
                    sendPhrase={onApplyPhrase}
                    onFillPhrasesText={noop}
                  />
                  <div style={{ marginLeft: '8px', marginTop: -4, fontSize: '18px' }}>
                    <TranslationButton
                      onClick={translateInput}
                      disabled={!inputText}
                      loading={inputTranslating}
                    />
                  </div>
                </div>
                <PassivePopover
                  mercadoQAmode
                  visible={focus}
                  placement="topLeft"
                  isRelateRegion={isRelateRegion}
                  region={region}
                  input={inputText}
                  setInput={noop}
                  onPhraseSelected={noop}
                  onPressEnter={(phrase) => onApplyPhrase(phrase, false)}
                  onFillPhraseText={noop}
                  onContentClick={() => {
                    setFocus(true);
                    contentClickRef.current = true;
                  }}
                >
                  <div ref={triggerRef}>
                    <Input.TextArea
                      className="!border-none"
                      maxLength={MAX_LENGTH}
                      showCount
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      rows={10}
                      autoSize={false}
                      onFocus={() => setFocus(true)}
                    />
                  </div>
                </PassivePopover>
              </div>
              {MERCADO_QA_LLM && questionItem.dialogReply?.isProductQuestion && (
                <div className="mt-2">
                  <Checkbox
                    checked={saveAsItemKnowledge}
                    onChange={({ target: { checked } }) => setSaveAsItemKnowledge(checked)}
                  >
                    {t('保存至知识库')}
                  </Checkbox>
                </div>
              )}
              <div className="flex gap-3 mt-3">
                <Button
                  type="primary"
                  size="small"
                  onClick={submitAnswer}
                  loading={submitting}
                  disabled={
                    !inputText || inputText.length > MAX_LENGTH || translateAndAnswerLoading
                  }
                >
                  {t('ViewStartups.reset.form.submit.btn')}
                </Button>
                <Button
                  size="small"
                  onClick={translateAndAnswer}
                  loading={translateAndAnswerLoading}
                  disabled={!inputText || inputText.length > MAX_LENGTH || submitting}
                >
                  {t('翻译后提交')}
                </Button>
                <Button
                  type="default"
                  size="small"
                  onClick={() => {
                    setMode('view');
                  }}
                  disabled={submitting}
                >
                  {t('取消')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-[var(--fett-text-tertiary)] leading-5 w-full">
              {questionItem?.answer?.text ? (
                <span className="w-full flex">
                  <span className="">{answer}</span>
                  <span className="flex-none mx-4 text-center">
                    <TranslationButton active onClick={toggleAnswer} loading={answerTranslating} />
                  </span>
                </span>
              ) : (
                <span className="flex items-start gap-3">
                  <span className="h-5">{t('conv_filter_advance_wait_reply')}</span>
                  {!preview &&
                    (qa.item.itemStatus === 'NORMAL' ? (
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        size="small"
                        className="!px-0 !h-5 -mt-0.5"
                        onClick={() => {
                          setMode('input');
                          setSaveAsItemKnowledge(
                            localStorage.getItem(saveAsItemKnowledgeStoreKey) === 'true',
                          );
                        }}
                      >
                        {t('添加答案')}
                      </Button>
                    ) : (
                      <Tooltip title={t('商品不在售，暂无法回答该商品问题')}>
                        <Button
                          disabled
                          type="link"
                          icon={<PlusOutlined />}
                          size="small"
                          className="!px-0 !h-5 -mt-0.5"
                        >
                          {t('添加答案')}
                        </Button>
                      </Tooltip>
                    ))}
                </span>
              )}
              {questionItem.answer?.text && (
                <div style={{ marginTop: 12, fontSize: 12, fontWeight: 400 }}>
                  {t('回复人')}：
                  {questionItem.answer.answerOrigin === 'MERCADO_QA_ANSWER_ORIGIN_AI_AUTO'
                    ? t('AI 自动回答')
                    : questionItem.answer.answerOrigin ===
                      'MERCADO_QA_ANSWER_ORIGIN_EXTERNAL_PLATFORM'
                    ? '-'
                    : questionItem.answer.userNick || '-'}
                  <div style={{ display: 'inline-block', width: 28 }} />
                  {MERCADO_QA_LLM && (
                    <>
                      {t('回复来源')}：
                      {answerOriginLabelMap[questionItem.answer.answerOrigin] || '-'}
                      <div style={{ display: 'inline-block', width: 28 }} />
                    </>
                  )}
                  {t('回复时间')}：
                  {dayjs(Number(questionItem.answer.createTime)).format('YYYY-MM-DD HH:mm:ss')}
                  <div style={{ display: 'inline-block', width: 28 }} />
                  {t('响应时间')}：
                  {renderStill(
                    Number(questionItem.createTime),
                    Number(questionItem.answer.createTime),
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

const DialogReply = styled.div`
  padding: 12px;
  padding-top: 4px;
  margin-bottom: 14px;
  background: #f0faf5;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    > h4 {
      margin-bottom: 0;
    }
    > div {
      display: flex;
      align-items: center;
    }
  }
  > .ant-divider {
    margin: 8px 0;
    & + p {
      color: #84868c;
    }
  }
  > p {
    margin-bottom: 0;
  }
`;

export default QuestionItem;
